// Messen mit Bezugspunkten (wie die Inferenz in SketchUp bzw. die
// „notable points" in Shapr3D): Ecken, Kantenmitten, Flächenmitten und
// Zentrum jedes Teils rasten beim Antippen ein — gemessen wird zwischen
// exakten Geometriepunkten, nicht zwischen freien Fingertipps. Engine-frei.
import { rotateLocalVector } from "./snapping"

// Alle Bezugspunkte eines (rotierten) Quader-Teils in Weltkoordinaten
export function partReferencePoints(part) {
  const h = [ part.length_mm / 2, part.thickness_mm / 2, part.width_mm / 2 ]
  const locals = []

  // 8 Ecken
  for (const sx of [ -1, 1 ]) for (const sy of [ -1, 1 ]) for (const sz of [ -1, 1 ]) {
    locals.push({ p: [ sx * h[0], sy * h[1], sz * h[2] ], kind: "corner" })
  }
  // 12 Kantenmitten: je Achse eine Null-Komponente
  for (const axis of [ 0, 1, 2 ]) {
    const [ a, b ] = [ 0, 1, 2 ].filter((i) => i !== axis)
    for (const sa of [ -1, 1 ]) for (const sb of [ -1, 1 ]) {
      const p = [ 0, 0, 0 ]
      p[a] = sa * h[a]
      p[b] = sb * h[b]
      locals.push({ p, kind: "edge" })
    }
  }
  // 6 Flächenmitten + Zentrum
  for (const axis of [ 0, 1, 2 ]) {
    for (const s of [ -1, 1 ]) {
      const p = [ 0, 0, 0 ]
      p[axis] = s * h[axis]
      locals.push({ p, kind: "face" })
    }
  }
  locals.push({ p: [ 0, 0, 0 ], kind: "center" })

  return locals.map(({ p, kind }) => {
    const [ x, y, z ] = rotateLocalVector(part, p)
    return { x: part.pos_x + x, y: part.pos_y + y, z: part.pos_z + z, kind }
  })
}

// Angetippten Punkt auf den nächsten Bezugspunkt aller Teile fangen;
// außerhalb der Toleranz bleibt es ein freier Punkt (kind "free")
export function snapMeasurePoint(parts, point, tolerance) {
  let best = null
  for (const part of parts) {
    for (const ref of partReferencePoints(part)) {
      const dist = Math.hypot(ref.x - point.x, ref.y - point.y, ref.z - point.z)
      if (dist <= tolerance && (!best || dist < best.dist)) best = { ...ref, dist }
    }
  }
  if (best) return { x: best.x, y: best.y, z: best.z, kind: best.kind }
  return { x: point.x, y: point.y, z: point.z, kind: "free" }
}

// --- Element-Messung: Punkt/Kante wählen, Winkel und Min/Max-Abstand -------

// Die 12 Kanten eines (rotierten) Quader-Teils als Weltsegmente
export function partEdges(part) {
  const h = [ part.length_mm / 2, part.thickness_mm / 2, part.width_mm / 2 ]
  const world = (p) => {
    const [ x, y, z ] = rotateLocalVector(part, p)
    return { x: part.pos_x + x, y: part.pos_y + y, z: part.pos_z + z }
  }
  const edges = []
  for (const axis of [ 0, 1, 2 ]) {
    const [ u, v ] = [ 0, 1, 2 ].filter((i) => i !== axis)
    for (const su of [ -1, 1 ]) for (const sv of [ -1, 1 ]) {
      const a = [ 0, 0, 0 ], b = [ 0, 0, 0 ]
      a[u] = b[u] = su * h[u]
      a[v] = b[v] = sv * h[v]
      a[axis] = -h[axis]
      b[axis] = h[axis]
      edges.push({ a: world(a), b: world(b) })
    }
  }
  return edges
}

const sub = (p, q) => [ p.x - q.x, p.y - q.y, p.z - q.z ]
const dot = (u, v) => u[0] * v[0] + u[1] * v[1] + u[2] * v[2]
const dist = (p, q) => Math.hypot(p.x - q.x, p.y - q.y, p.z - q.z)

// Nächste Punkte zweier Segmente (Ericson, Real-Time Collision Detection)
function closestPointsSegments(p1, q1, p2, q2) {
  const d1 = sub(q1, p1), d2 = sub(q2, p2), r = sub(p1, p2)
  const a = dot(d1, d1), e = dot(d2, d2), f = dot(d2, r)
  let s, t
  if (a <= 1e-9 && e <= 1e-9) { s = 0; t = 0 }
  else if (a <= 1e-9) { s = 0; t = Math.max(0, Math.min(1, f / e)) }
  else {
    const c = dot(d1, r)
    if (e <= 1e-9) { t = 0; s = Math.max(0, Math.min(1, -c / a)) }
    else {
      const b = dot(d1, d2)
      const denom = a * e - b * b
      s = denom > 1e-9 ? Math.max(0, Math.min(1, (b * f - c * e) / denom)) : 0
      t = (b * s + f) / e
      if (t < 0) { t = 0; s = Math.max(0, Math.min(1, -c / a)) }
      else if (t > 1) { t = 1; s = Math.max(0, Math.min(1, (b - c) / a)) }
    }
  }
  const at = (p, d, k) => ({ x: p.x + d[0] * k, y: p.y + d[1] * k, z: p.z + d[2] * k })
  return { a: at(p1, d1, s), b: at(p2, d2, t) }
}

function closestPointOnSegment(p, a, b) {
  const d = sub(b, a)
  const len = dot(d, d)
  const t = len <= 1e-9 ? 0 : Math.max(0, Math.min(1, dot(sub(p, a), d) / len))
  return { x: a.x + d[0] * t, y: a.y + d[1] * t, z: a.z + d[2] * t }
}

// Element unter dem Finger: Eckpunkte, Flächenmitten und Zentrum (und die
// Kantenmitte in engerer Toleranz) fangen als Punkt; sonst greift die ganze
// Kante als Element; abseits der Geometrie bleibt ein freier Punkt
export function pickMeasureElement(parts, point, tolerance) {
  let bestPoint = null
  for (const part of parts) {
    for (const ref of partReferencePoints(part)) {
      const d = dist(ref, point)
      const limit = ref.kind === "edge" ? tolerance * 0.75 : tolerance
      if (d <= limit && (!bestPoint || d < bestPoint.d)) bestPoint = { ref, d }
    }
  }
  if (bestPoint) {
    const { x, y, z, kind } = bestPoint.ref
    return { type: "point", kind, point: { x, y, z } }
  }

  let bestEdge = null
  for (const part of parts) {
    for (const edge of partEdges(part)) {
      const d = dist(point, closestPointOnSegment(point, edge.a, edge.b))
      if (d <= tolerance && (!bestEdge || d < bestEdge.d)) bestEdge = { edge, d }
    }
  }
  if (bestEdge) return { type: "edge", a: bestEdge.edge.a, b: bestEdge.edge.b }

  return { type: "point", kind: "free", point: { x: point.x, y: point.y, z: point.z } }
}

// Messwerte zwischen zwei Elementen: Punktpaar → Abstand + Achsen-Deltas;
// sobald eine Kante beteiligt ist → Min-/Max-Abstand, bei zwei Kanten
// zusätzlich der (unorientierte) Winkel
export function measureBetween(e1, e2) {
  if (e1.type === "point" && e2.type === "point") {
    const d = dist(e1.point, e2.point)
    return {
      kind: "points", closest: { a: e1.point, b: e2.point },
      min: d, max: d, angleDeg: null,
      delta: {
        x: Math.abs(e1.point.x - e2.point.x),
        y: Math.abs(e1.point.y - e2.point.y),
        z: Math.abs(e1.point.z - e2.point.z)
      }
    }
  }

  if (e1.type === "edge" && e2.type === "edge") {
    const closest = closestPointsSegments(e1.a, e1.b, e2.a, e2.b)
    let max = 0
    for (const p of [ e1.a, e1.b ]) for (const q of [ e2.a, e2.b ]) max = Math.max(max, dist(p, q))
    const u = sub(e1.b, e1.a), v = sub(e2.b, e2.a)
    const cos = Math.abs(dot(u, v)) / (Math.sqrt(dot(u, u)) * Math.sqrt(dot(v, v)) || 1)
    return {
      kind: "edges", closest,
      min: dist(closest.a, closest.b), max,
      angleDeg: Math.round(Math.acos(Math.min(1, cos)) * (180 / Math.PI) * 10) / 10,
      delta: null
    }
  }

  const point = e1.type === "point" ? e1.point : e2.point
  const edge = e1.type === "edge" ? e1 : e2
  const near = closestPointOnSegment(point, edge.a, edge.b)
  return {
    kind: "pointEdge",
    closest: e1.type === "point" ? { a: point, b: near } : { a: near, b: point },
    min: dist(point, near),
    max: Math.max(dist(point, edge.a), dist(point, edge.b)),
    angleDeg: null, delta: null
  }
}

// Anzeigetext fürs Maßlabel (X = Länge, H = Höhe, T = Tiefe)
export function formatMeasureText(result) {
  const mm = (v) => `${Math.round(v)} mm`
  if (result.kind === "points") {
    const d = result.delta
    return `${mm(result.min)} (ΔX ${Math.round(d.x)} · ΔH ${Math.round(d.y)} · ΔT ${Math.round(d.z)})`
  }
  if (result.kind === "edges") {
    return `∠ ${result.angleDeg}° · min ${mm(result.min)} · max ${mm(result.max)}`
  }
  return `min ${mm(result.min)} · max ${mm(result.max)}`
}
